<template>
  <div class="fixed bottom-0 right-0 flex flex-col items-end ml-6 w-80">
    <div
      v-if="chatBoxIsOpened"
      class="mr-5 flex flex-col mb-5 shadow-none w-full"
    >
      <!-- close button -->
      <div
        @click.prevent="closeChatBox"
        class="bg-red-500 hover:bg-red-600 text-white mb-1 w-10 flex justify-center items-center px-2 py-1 rounded self-end cursor-pointer"
      >
        <svg width="1em" height="1em" viewBox="0 0 16 16" class="bi bi-x" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" d="M11.854 4.146a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708-.708l7-7a.5.5 0 0 1 .708 0z"/>
          <path fill-rule="evenodd" d="M4.146 4.146a.5.5 0 0 0 0 .708l7 7a.5.5 0 0 0 .708-.708l-7-7a.5.5 0 0 0-.708 0z"/>
        </svg>
      </div>

      <!-- admin profile -->
      <div class="flex justify-between items-center text-white p-2 bg-blue-500 border shadow-lg mr-5 w-full">
        <div class="flex items-center">
          <span class="font-semibold tracking-wider" v-text="`${$t('messages.online')}:`"></span>
          <span class="ml-1 font-normal" v-text="$tc('messages.online_catchers', onlineCatchers, {number: onlineCatchers})"></span>
        </div>
      </div>

      <!-- chats -->

      <div
        v-chat-scroll="{ smooth: true, notSmoothOnInit: true }"
        class="flex flex-col bg-gray-200 px-2 shadow-lg overflow-auto h-75"
        :class="{'justify-end': fetchingMessages}"
      >
        <PulseLoader
          :loading="fetchingMessages"
          color="#000"
          size="10px"
          class="text-center my-4"
        />

        <div
          v-for="(message, index) in messages"
          :key="`message_${index}`"
          class="message p-2 my-2 rounded-md shadow ml-3"
          :class="[authUser.id === message.author.id ? 'bg-green-500 text-white self-end' : 'bg-white text-gray-700 self-start']"
        >
          <strong v-text="message.author.username"></strong>
          <p v-text="message.content"></p>
          <div class="flex justify-end">
            <abbr
              :title="message.created_at.datetime_string"
              v-text="message.created_at.human_readable"
              class="text-xs"
            >
            </abbr>
          </div>
        </div>
      </div>

      <!-- send message -->
      <form
        @submit.prevent="save"
        class="relative bg-white"
        autocomplete="off"
      >
        <input type="text"
               name="message"
               :placeholder="$t('messages.type_your_message_here')"
               class="pl-4 pr-16 py-2 border border-blue-500 focus:outline-none w-full"
               v-model="message"
        >
        <button
          class="absolute right-0 bottom-0 text-blue-600 bg-white  hover:text-blue-500 m-1 px-3 py-1 w-auto transistion-color duration-100 focus:outline-none"
          type="submit"
          v-text="$t('messages.send')"
        >
        </button>
      </form>
    </div>

    <div
      v-if="chatBoxIsClosed"
      @click.prevent="openChatBox"
      class="mx-10 mb-6 mt-4 text-green-500 hover:text-green-600 flex justify-center items-center cursor-pointer"
    >
      <svg width="4em" height="4em" viewBox="0 0 16 16" class="bi bi-chat-text-fill" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" d="M16 8c0 3.866-3.582 7-8 7a9.06 9.06 0 0 1-2.347-.306c-.584.296-1.925.864-4.181 1.234-.2.032-.352-.176-.273-.362.354-.836.674-1.95.77-2.966C.744 11.37 0 9.76 0 8c0-3.866 3.582-7 8-7s8 3.134 8 7zM4.5 5a.5.5 0 0 0 0 1h7a.5.5 0 0 0 0-1h-7zm0 2.5a.5.5 0 0 0 0 1h7a.5.5 0 0 0 0-1h-7zm0 2.5a.5.5 0 0 0 0 1h4a.5.5 0 0 0 0-1h-4z"/>
      </svg>
    </div>

  </div>
</template>

<script>
import {mapGetters, mapState, mapActions} from 'vuex';
import PulseLoader from 'vue-spinner/src/PulseLoader.vue';

export default {
  components: {
    PulseLoader
  },

  computed: {
    ...mapState('auth', {
      authChecked: state => state.checked,
      authUser: state => state.user,
    }),
    ...mapGetters('chat', [
      'chatBoxIsOpened',
      'chatBoxIsClosed',
      'fetchingMessages',
      'currentMessage',
      'messages',
      'onlineCatchers',
    ]),
    ...mapGetters('civium', [
      'currentExperimentId'
    ]),

    message: {
      set(message) {
        this.$store.dispatch('chat/setCurrentMessage', message);
      },
      get() {
        return this.currentMessage;
      }
    },
  },

  methods: {
    ...mapActions('chat', [
      'openChatBox',
      'closeChatBox',
      'fetchMessages',
      'sendMessage',
      'receivedMessage',
      'userLoggedOn',
      'userLoggedOff',
      'updateOnlineUsers',
    ]),

    save() {
      this.sendMessage({
        experiment: this.currentExperimentId
      })
    },

    handleBrowserClose(event) {
      this.userLoggedOff({experiment: this.currentExperimentId});
    }
  },

  mounted() {
    this.userLoggedOn({experiment: this.currentExperimentId});
    this.fetchMessages({experiment: this.currentExperimentId});
    this.updateOnlineUsers({experiment: this.currentExperimentId});

    window.Echo
      .channel(`experiment-chatroom.${this.currentExperimentId}`)
      .listen('.chat-message.recieved', (e) => {
        this.receivedMessage({message: e})
      })
      .listen('.profile.online', (e) => {
        this.updateOnlineUsers({experiment: this.currentExperimentId})
      })
      .listen('.profile.offline', (e) => {
        this.updateOnlineUsers({experiment: this.currentExperimentId})
      });
  },

  created() {
    window.addEventListener('beforeunload', this.handleBrowserClose)
  }
}
</script>

<style scoped>
.message {
  max-width: 250px;
}
</style>
